import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { Box, CircularProgress, Typography } from "@mui/material";
import { ACTIONS, PATHS } from "lib/routing";
import { mobileNavigate } from "lib/routing/navigate/navigate";
import { CEQMSQuestionProps } from "./SpecialtyQuestionnairePage.types";
import { actualiseAppointment } from "api/appointment/ActualiseAppontment/actualiseAppontment";
import ButtonsFooter from "lib/components/buttons/ButtonsFooter/ButtonsFooter";
import VerticalSpreadLayout from "lib/components/layout/VerticalSpreadLayout/VerticalSpreadLayout";
import ConfirmationModal from "lib/components/modals/ConfirmationModal/ConfirmationModal";
import { sxStyles } from "./SpecialtyQuestionnairePage.styles";
import MapRawStringToReactElement from "lib/util/ReactComponentUtil/mapRawStringToReactElement/MapRawStringtoReactElement";
import { checkIn } from "api/appointment/CheckIn/checkIn";
import IMAGES from "lib/assets/images";
import { AxiosError } from "axios";
import { useAppDispatch, useAppSelector } from "lib/redux/hooks";
import {
  selectAppointmentMeta,
  selectAppointments,
  selectApptDetail,
} from "ui/appointment/ducks/selectors";
import { selectUser } from "lib/redux/user/selectors";
import { updateQueueForActualiseAppointment } from "ui/appointment/ducks";
import { handleErrorModal } from "lib/redux/notifications";
import {
  setKioskCEQMSQuestionnaireAnswer,
  updateCheckInSuccessState,
} from "ui/appointment/ducks/appointmentsSlice";
import CEQMSQuestion from "../CEQMSQuestion/CEQMSQuestion";
import { KioskAnswer } from "api/appointment/ActualiseAppontment/actualiseAppontment.fromUi.types";
import { CEQMSQuestion as CEQMSQuestionType } from "api/appointment/GetKioskQuestionnaire/getKioskQuestionnaire.Api.types";
import { CEQMSQuestionsAnswers } from "ui/appointment/ducks/appointments.types";
import { isNUP } from "lib/util/ValidatorUtil/isNupValidator/isNupValidator";

const TITLES = {
  REGISTRATION_SUCCESS: "Registration Successful",
  CHECKIN_SUCCESS: "Welcome to Our Clinic",
  CHECKIN_WARNING: "Oops...",
};

const SpecialtyQuestionnairePage = ({ isCheckIn }: CEQMSQuestionProps) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const classes = sxStyles();

  // Redux States
  const {
    logoCode,
    appointmentId,
    regInstitutionCode,
    institutionCode,
    clinicCode,
    institutionName,
    consultType,
    regAppointmentId,
    datetimeString: datetimeStringMeta,
    vcSupportStatus,
  } = useAppSelector(selectAppointmentMeta);
  const { encounterId = null, appointmentTimeToArrive } =
    useAppSelector(selectApptDetail) || {};
  const currentAppointmentTime = datetimeStringMeta || appointmentTimeToArrive;
  const { memberIdentifier } = useAppSelector(selectUser);

  // States for Appointment Actualization
  const [isActualizing, setIsActualizing] = useState(false);
  // States for Check In
  const [isCheckingIn, setIsCheckingIn] = useState(false);
  // States & Handlers for Confirmation Dialog, after Actualizing an appointment successfully
  const [confirmationTitle, setConfirmationTitle] = useState<string | null>();
  const [confirmationText, setConfirmationText] = useState<string | null>();
  const [openConfirmation, setOpenConfirmation] = useState(false);

  const handleOpenConfirmation = () => setOpenConfirmation(true);

  const onActualisedAppointment = (
    appointmentId: string,
    queueNumber: string,
    institutionName: string | null,
    consultType: string | null,
    encounterId: string | null,
    appointmentTime: string | null,
    logoCode: string | null,
  ) => {
    dispatch(
      updateQueueForActualiseAppointment({
        appointmentId,
        queueNumber,
        institutionName,
        consultType,
        encounterId,
        appointmentTime,
        logoCode,
      }),
    );
  };

  // Handlers
  const handleQuestionnaireChange = (
    isChecked: boolean,
    answer: string,
    isRadio: boolean,
    questionId: string,
  ) => {
    let questionAnswers =
      ceqmsQuestionsAnswers && ceqmsQuestionsAnswers[questionId]
        ? ceqmsQuestionsAnswers[questionId]
        : [];

    if (isChecked && isRadio) {
      let answerMap: CEQMSQuestionsAnswers = { [questionId]: [answer] };
      dispatch(setKioskCEQMSQuestionnaireAnswer(answerMap));
    } else if (isChecked) {
      let updated = [...questionAnswers, answer];
      let answerMap: CEQMSQuestionsAnswers = { [questionId]: updated };
      dispatch(setKioskCEQMSQuestionnaireAnswer(answerMap));
    } else {
      var index = questionAnswers.indexOf(answer);
      let updated = questionAnswers.toSpliced(index, 1);
      let answerMap: CEQMSQuestionsAnswers = { [questionId]: updated };
      dispatch(setKioskCEQMSQuestionnaireAnswer(answerMap));
    }
  };

  const handleCloseConfirmation = () => setOpenConfirmation(false);
  const handleErrorModalAction = (open: boolean, message: string | null) => {
    dispatch(handleErrorModal({ open, message }));
  };
  const handleSuccessModal = (
    checkInSuccessStatus: boolean,
    checkInSuccessTitleIcon: "checkIcon" | "errorIcon" | null,
    title: string | null,
    message: string | null,
  ) => {
    dispatch(
      updateCheckInSuccessState({
        checkInSuccessStatus,
        checkInSuccessTitleIcon,
        title,
        message,
      }),
    );
  };

  const isVirtualConsultAppointment = vcSupportStatus === "supported";

  // Retrieve Kiosk Questionnaire from the remote API & Handle states for recorded Answers
  const ceqmsQuestions =
    useAppSelector(selectAppointments).kioskQuestionnaire.ceqmsQuestions
      ?.Specialty;

  const shouldGoToPayment =
    (
      useAppSelector(selectAppointments).kioskQuestionnaire.ceqmsQuestions
        ?.Payment ?? []
    ).length > 0;
  const ceqmsQueueNumber =
    useAppSelector(selectAppointments).kioskQuestionnaire.queueNumber;
  const ceqmsQuestionsAnswers =
    useAppSelector(selectAppointments).kioskQuestionnaire.ceqmsQuestionsAnswers;
  const isMobile =
    useAppSelector(selectAppointments).kioskQuestionnaire.isFromMobile;

  // Send Appointment Registration Request
  // Actualise Appointment and Submit Questionnaire in the process
  const sendActualiseAppointmentRequest = async () => {
    try {
      if (
        appointmentId &&
        regInstitutionCode &&
        regAppointmentId &&
        ceqmsQuestions
      ) {
        // Make the request
        setIsActualizing(true);

        const actualiseAppointmentPayload = await actualiseAppointment({
          MemberIdentifier: memberIdentifier,
          RegAppointmentId: regAppointmentId,
          RegInstitutionCode: regInstitutionCode,
          InstitutionCode: institutionCode,
          ClinicCode: clinicCode,
          QuestionnaireResponse: ceqmsQuestionsAnswers
            ? Object.entries(ceqmsQuestionsAnswers).map(
                ([key, value]: [string, string[]]) => {
                  return {
                    QuestionId: key,
                    Response: null,
                    ResponseArray: value,
                  } satisfies KioskAnswer;
                },
              )
            : [],
          VcSupportStatus: vcSupportStatus,
          IsCEQMS: true,
        });

        // Update the queue number and encounterid, so that it can be used in the next step
        onActualisedAppointment(
          appointmentId,
          actualiseAppointmentPayload.QueueNumber,
          institutionName,
          consultType,
          actualiseAppointmentPayload.EncounterId,
          currentAppointmentTime,
          logoCode,
        );

        // redirect back to appointment landing page for virtual consult appointment
        if (isVirtualConsultAppointment) {
          navigate(PATHS.APPOINTMENT_MOBILE.path);
        } else if (!isNUP(institutionCode, logoCode)) {
          navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
        } else {
          setConfirmationTitle(TITLES.REGISTRATION_SUCCESS);
          setConfirmationText(actualiseAppointmentPayload.ErrorText);
          handleOpenConfirmation();
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        handleErrorModalAction(true, error.response?.data.Message);
      } else {
        handleErrorModalAction(true, null);
      }
    } finally {
      setIsActualizing(false);
    }
  };

  // Send CheckIn Request
  // CheckIn and Submit Questionnaire in the process
  const sendCheckInRequest = async () => {
    try {
      if (regAppointmentId && regInstitutionCode && ceqmsQuestions) {
        // Make the request
        setIsCheckingIn(true);

        const checkInPayload = await checkIn({
          MemberIdentifier: memberIdentifier,
          RegAppointmentId: regAppointmentId,
          EncounterId: encounterId,
          AppointmentTime: currentAppointmentTime,
          QuestionnaireResponse: ceqmsQuestionsAnswers
            ? Object.entries(ceqmsQuestionsAnswers).map(
                ([key, value]: [string, string[]]) => {
                  return {
                    QuestionId: key,
                    Response: null,
                    ResponseArray: value,
                  } satisfies KioskAnswer;
                },
              )
            : [],
          IsCEQMS: true,
        });

        if (checkInPayload.EncounterStatus === "S") {
          handleSuccessModal(
            true,
            "checkIcon",
            TITLES.CHECKIN_SUCCESS,
            checkInPayload.Message,
          );
          navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
        } else if (checkInPayload.EncounterStatus === "W") {
          handleSuccessModal(
            true,
            "errorIcon",
            TITLES.CHECKIN_WARNING,
            checkInPayload.ErrorText,
          );
          navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
        }
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        handleErrorModalAction(true, error.response?.data.Message);
      } else {
        handleErrorModalAction(true, null);
      }
    } finally {
      setIsCheckingIn(false);
    }
  };

  useEffect(() => {
    if (isVirtualConsultAppointment) {
      sendActualiseAppointmentRequest();
    } else if (ceqmsQueueNumber) {
      // If there is already the queue number return from getKioskQuestionnaire, just use the queue number and no calling of actualizeAppointment
      onActualisedAppointment(
        appointmentId || "",
        ceqmsQueueNumber,
        institutionName,
        consultType,
        encounterId || "",
        currentAppointmentTime || "",
        logoCode,
      );
      navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ceqmsQueueNumber]);

  return (
    <VerticalSpreadLayout>
      {isActualizing || isCheckingIn ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mt: 4,
          }}
        >
          <CircularProgress />
        </Box>
      ) : ceqmsQuestions && !isVirtualConsultAppointment ? (
        <>
          <Box sx={{ px: 2, pt: 1, mb: 4 }}>
            <Box sx={{ mt: 3 }}>
              <Box sx={classes.requireBox}>
                <Box
                  component={"img"}
                  src={IMAGES.general.InformationErrorIcon}
                />
                <Typography component={"span"} sx={classes.requireText}>
                  All fields required
                </Typography>
              </Box>
            </Box>
            {ceqmsQuestions?.map((question, i) => {
              return (
                <Box key={question.QuestionId}>
                  <CEQMSQuestion
                    questionNumber={i}
                    questionId={question.QuestionId}
                    questionText={question.QuestionEnglish}
                    choices={question.Choices}
                    questionType={question.QuestionType}
                    handleQuestionnaireChange={(isChecked, answer, isRadio) =>
                      handleQuestionnaireChange(
                        isChecked,
                        answer,
                        isRadio,
                        question.QuestionId,
                      )
                    }
                    required={question.Required}
                  />
                </Box>
              );
            })}
          </Box>
          <Box sx={{ px: 2, pb: 2.5 }}>
            <ButtonsFooter
              nextButtonText="Next"
              cancelButtonText="Back"
              isDisabled={
                isActualizing ||
                isCheckingIn ||
                !areAllQuestionsAnswered(ceqmsQuestions, ceqmsQuestionsAnswers)
              }
              onClickNext={() => {
                if (shouldGoToPayment && isCheckIn) {
                  navigate(PATHS.APPOINTMENT_PAYMENT_CHECKIN.path);
                } else if (shouldGoToPayment && !isCheckIn) {
                  navigate(PATHS.APPOINTMENT_REGISTER_PAYMENT_KIOSK.path);
                } else if (isCheckIn) {
                  sendCheckInRequest();
                } else if (encounterId) {
                  navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
                } else {
                  sendActualiseAppointmentRequest();
                }
              }}
              onClickCancel={() => {
                if (isMobile) {
                  mobileNavigate(ACTIONS.MOBILE_DASHBOARD_PATH);
                } else {
                  navigate(-1);
                }
              }}
            />
          </Box>

          {/* After Successful Actualization */}
          <ConfirmationModal
            open={openConfirmation}
            hideCancelButton={true}
            title={confirmationTitle || ""}
            body={MapRawStringToReactElement(confirmationText || "")}
            nextButtonText="OK"
            onClickNext={() => {
              // proceed next after successful actualisation
              navigate(PATHS.APPOINTMENT_QUEUECHIT_JOURNEY_NO_BACK.path);
            }}
            onClose={handleCloseConfirmation}
          />
        </>
      ) : null}
    </VerticalSpreadLayout>
  );
};

export const areAllQuestionsAnswered = (
  ceqmsQuestions: CEQMSQuestionType[],
  ceqmsQuestionsAnswers?: CEQMSQuestionsAnswers,
) => {
  if (ceqmsQuestions.length > 0) {
    if (ceqmsQuestionsAnswers) {
      // at least 1 answer hasn't been answered yet
      for (let i = 0; i < ceqmsQuestions.length; i++) {
        const question = ceqmsQuestions[i];
        if (
          question.Required &&
          (!ceqmsQuestionsAnswers[question.QuestionId] ||
            ceqmsQuestionsAnswers[question.QuestionId].length < 1)
        ) {
          return false;
        }
      }
    } else {
      return false;
    }
    return true;
  } else {
    return true;
  }
};

export default SpecialtyQuestionnairePage;

export const exportForTesting = {
  areAllQuestionsAnswered,
};
